import { useEffect, useState } from 'react';

import { useNavigate, useSearchParams, Link } from 'react-router-dom';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import TextInput from '#globalComponents/Forms/TextInput';
import { useAuth } from '#globalContexts/AuthProvider';

import registerBackground from '../../../../assets/images/register-background.jpg';

const ConfirmPasswordResetPage = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [error, setError] = useState('');
	const [success, setSuccess] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [formData, setFormData] = useState({
		password: '',
		passwordRepeat: '',
	});
	const { updatePassword } = useAuth();

	// Better Auth redirects the reset link here with ?token=…, or ?error=INVALID_TOKEN
	// when it is expired or already used.
	const token = searchParams.get('token');
	useEffect(() => {
		if (searchParams.get('error') || !token) setError('Link abgelaufen oder ungültig.');
	}, [searchParams, token]);

	const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		setSuccess(false);

		if (formData.password !== formData.passwordRepeat) {
			setError('Passwörter stimmen nicht überein.');
			return;
		}

		if (formData.password.length < 6) {
			setError('Passwort muss mindestens 6 Zeichen lang sein.');
			return;
		}

		setIsLoading(true);
		const { error } = await updatePassword(formData.password, token);
		setIsLoading(false);

		if (error) setError(error.message);
		else {
			setSuccess(true);
			//Redirect after seeing success message
			setTimeout(() => navigate('/login'), 3000);
		}
	};

	return (
		<div
			className="min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center relative"
			style={{
				backgroundImage: `url(${registerBackground})`,
			}}
		>
			{/* Overlay with blur effect */}
			<div className="absolute inset-0 bg-black bg-opacity-30 backdrop-blur-sm" />

			<div className="relative z-10">
				<Card className="p-8 max-w-md w-full mx-4">
					{/* Error state - invalid/expired link */}
					{!token && error && (
						<>
							<div className="text-center mb-6">
								<div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
									<svg
										className="w-8 h-8 text-warning-500"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
										/>
									</svg>
								</div>
								<h1 className="text-lg font-bold text-text mb-2">Link ungültig</h1>
								<p className="text-text mb-6">
									Der Link ist abgelaufen oder ungültig. Bitte fordern Sie einen neuen Link an.
								</p>
							</div>

							<Button href="/forgot-password" variant="solid" size="md" fullWidth>
								Neuen Link anfordern
							</Button>

							<div className="text-center mt-4">
								<Link
									to="/login"
									className="text-sm text-primary-700 hover:text-primary-700 font-medium flex items-center justify-center"
								>
									<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M10 19l-7-7m0 0l7-7m-7 7h18"
										/>
									</svg>
									Zurück zur Anmeldung
								</Link>
							</div>
						</>
					)}

					{/* Success state */}
					{success && (
						<>
							<div className="text-center mb-6">
								<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
									<svg className="w-8 h-8 text-success-500" fill="currentColor" viewBox="0 0 20 20">
										<path
											fillRule="evenodd"
											d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
											clipRule="evenodd"
										/>
									</svg>
								</div>
								<h1 className="text-lg font-bold text-text mb-2">Passwort aktualisiert!</h1>
								<p className="text-text">
									Ihr Passwort wurde erfolgreich geändert. Sie werden automatisch zur Anmeldung
									weitergeleitet.
								</p>
							</div>

							<div className="flex items-center justify-center">
								<div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mr-2"></div>
								<span className="text-sm text-text">Weiterleitung...</span>
							</div>
						</>
					)}

					{/* Form state */}
					{token && !success && (
						<>
							<div className="text-center mb-6">
								<h1 className="text-lg font-bold text-text mb-2">Neues Passwort setzen</h1>
								<p className="text-text">Geben Sie Ihr neues Passwort ein</p>
							</div>

							{error && (
								<div className="bg-red-50 border border-red-200 text-warning-700 px-4 py-3 rounded-lg mb-4">
									{error}
								</div>
							)}

							<form onSubmit={handleSubmit}>
								<div className="mb-4">
									<TextInput
										type="password"
										name="password"
										value={formData.password}
										onChange={handleChange}
										placeholder="Neues Passwort (mindestens 6 Zeichen)"
										required
										minLength={6}
									/>
								</div>

								<div className="mb-6">
									<TextInput
										type="password"
										name="passwordRepeat"
										value={formData.passwordRepeat}
										onChange={handleChange}
										placeholder="Passwort wiederholen"
										required
									/>
								</div>

								<Button type="submit" variant="solid" size="md" fullWidth loading={isLoading}>
									Passwort aktualisieren
								</Button>
							</form>
						</>
					)}
				</Card>
			</div>
		</div>
	);
};

export default ConfirmPasswordResetPage;
